import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity, ArrowRight, BarChart3, Building2, FileText, FolderKanban, Map,
  Plus, Search, Sparkles,
} from "lucide-react";
import { LAYERS, T } from "../../app/tokens.js";
import {
  clearRecentViews,
  removeRecentView,
  searchUniversal,
} from "../../data/searchClient.js";
import {
  buildLocationUrl, buildLeaseUrl, buildMarketUrl, buildProjectUrl, viewToPath,
} from "../../services/buildUrl.js";

const ICONS = {
  office: Building2,
  market: Map,
  lease: FileText,
  project: FolderKanban,
  report: BarChart3,
  action: Activity,
  add: Plus,
  reports: BarChart3,
};

// Phase 2: re-enable when the palette has actionable commands.
const SHOW_PALETTE_ACTIONS = false;

const FIRST_VISIT_EXAMPLES = [
  { query: "London", note: "an office, or a whole city" },
  { query: "Angel Court", note: "a building or its lease" },
  { query: "Headcount Trend", note: "a report" },
  { query: "SFO-03", note: "an office ID, or part of one" },
];

const RETURNING_SEARCH_EXAMPLES = [
  { query: "San Francisco", note: "another office or city" },
  { query: "AMER-SFO-03", note: "an office ID, or part of one" },
  ...FIRST_VISIT_EXAMPLES.slice(0, 3),
  { query: "600 Battery", note: "a street address works too" },
];

const RETURNING_SEARCH_FALLBACKS = [
  { query: "Paris", note: "another office or city" },
  { query: "Space Fit", note: "a report" },
  { query: "100 Bishopsgate", note: "a building or its lease" },
  { query: "EMEA-LON-02", note: "an office ID, or part of one" },
];

const SEARCH_BROWSE_DESTINATIONS = [
  { key: "offices", label: "Offices", path: "/offices", icon: Building2 },
  { key: "leases", label: "Leases", path: "/leases", icon: FileText },
  { key: "reports", label: "Reports", path: "/reports", icon: BarChart3 },
];

const MAX_RECENT_RESULTS = 4;
const MAX_RETURNING_EXAMPLES = 2;
const GROUP_ORDER = ["offices", "leases", "projects", "markets", "reports"];

function normalizedText(value) {
  return String(value || "").trim().toLocaleLowerCase();
}

function exampleMatchesRecent(example, item) {
  const query = normalizedText(example.query);
  return [item.id, item.title, item.subtitle, item.context]
    .filter(Boolean)
    .some((value) => {
      const field = normalizedText(value);
      return field.includes(query) || query.includes(field);
    });
}

function getReturningExamples(recentItems) {
  const candidates = [...RETURNING_SEARCH_EXAMPLES, ...RETURNING_SEARCH_FALLBACKS];
  return candidates
    .filter((example) => !recentItems.some((item) => exampleMatchesRecent(example, item)))
    .slice(0, MAX_RETURNING_EXAMPLES);
}

function flattenGroups(groups) {
  return groups.flatMap((group) => group.results.map((item) => ({ ...item, groupKey: group.key, groupLabel: group.label })));
}

function destinationToUrl(destination) {
  if (!destination) return null;
  if (destination.kind === "office") return buildLocationUrl(destination.id);
  if (destination.kind === "market") return buildMarketUrl(destination.id);
  if (destination.kind === "lease") return buildLeaseUrl(destination.id);
  if (destination.kind === "project") return buildProjectUrl(destination.id);
  if (destination.kind === "report") return viewToPath(destination.key);
  if (destination.kind === "action" && destination.key === "go-to-reports") return "/reports";
  return null;
}

function formatStatus(status) {
  if (!status) return "";
  return String(status)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function isInactiveStatus(status) {
  return ["closed", "retired", "vacated", "inactive", "expired", "terminated"]
    .includes(String(status || "").toLowerCase());
}

export function UniversalSearchPalette({
  open,
  onClose,
  onNavigate,
  onAction,
  onAsk,
  anchored = false,
  query = "",
  inputRef,
  categoryVisibility = {},
}) {
  const [payload, setPayload] = useState({ groups: [] });
  const [status, setStatus] = useState("loading");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [mutationError, setMutationError] = useState(null);
  const [hoveredRecentKey, setHoveredRecentKey] = useState(null);
  const [focusedRecentKey, setFocusedRecentKey] = useState(null);
  const requestId = useRef(0);
  const choosing = useRef(false);
  const [payloadQuery, setPayloadQuery] = useState(null);

  const hasQuery = query.trim().length > 0;
  const visibleGroups = useMemo(
    () => {
      const groups = SHOW_PALETTE_ACTIONS
        ? (payload.groups || [])
        : (payload.groups || []).filter((group) => group.key !== "actions");
      const recentGroup = groups.find((group) => group.key === "recent");
      if (hasQuery) return [...groups].sort((a, b) =>
        GROUP_ORDER.indexOf(a.key) - GROUP_ORDER.indexOf(b.key));
      if (!recentGroup?.results?.length) return groups;
      return [
        { ...recentGroup, results: recentGroup.results.slice(0, MAX_RECENT_RESULTS) },
        ...groups.filter((group) => group !== recentGroup),
      ];
    },
    [hasQuery, payload.groups],
  );
  const allRecentItems = useMemo(
    () => (payload.groups || []).find((group) => group.key === "recent")?.results || [],
    [payload.groups],
  );
  const returningExamples = useMemo(
    () => getReturningExamples(allRecentItems),
    [allRecentItems],
  );
  const entityResults = useMemo(() =>
    status === "ready" && payloadQuery === query ? flattenGroups(visibleGroups) : [],
  [visibleGroups, status, payloadQuery, query]);
  const showAsk = hasQuery && typeof onAsk === "function";
  const results = useMemo(() => [
    ...(showAsk ? [{ type: "ask", id: "ask-portfolio" }] : []),
    ...entityResults,
  ], [showAsk, entityResults]);
  const activeIndex = Math.min(selectedIndex, Math.max(0, results.length - 1));
  const hasRecentViews = !hasQuery
    && (visibleGroups.find((group) => group.key === "recent")?.results?.length || 0) > 0;
  const choose = useCallback((item) => {
    if (!item || choosing.current) return;
    if (item.type === "ask") {
      choosing.current = true;
      onClose();
      onAsk?.(query.trim());
      return;
    }
    if (item.type === "action") {
      onAction?.(item.id, item);
      onClose();
      return;
    }
    const url = destinationToUrl(item.destination);
    if (url) {
      onNavigate?.(url, item);
      onClose();
    }
  }, [onAction, onClose, onNavigate, onAsk, query]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    setSelectedIndex(0);
    choosing.current = false;
    setMutationError(null);
    setHoveredRecentKey(null);
    setFocusedRecentKey(null);
    inputRef?.current?.focus();
    const activeInput = inputRef?.current;
    return () => {
      if (document.activeElement === activeInput) activeInput.blur();
    };
  }, [inputRef, open]);

  useEffect(() => { setSelectedIndex(0); }, [query, showAsk]);

  useEffect(() => {
    if (!open) return undefined;
    setStatus("loading");
    const id = ++requestId.current;
    const load = () => {
      searchUniversal(query)
        .then((next) => {
          if (id !== requestId.current) return;
          setPayload(next);
          setPayloadQuery(query);
          setSelectedIndex(0);
          setStatus("ready");
        })
        .catch(() => {
          if (id === requestId.current) setStatus("error");
        });
    };
    const timer = query === "" ? null : window.setTimeout(load, 120);
    if (query === "") load();
    return () => {
      if (timer !== null) window.clearTimeout(timer);
      if (requestId.current === id) requestId.current += 1;
    };
  }, [open, query, reloadKey]);

  useEffect(() => {
    if (!open || !inputRef?.current) return undefined;
    const input = inputRef.current;
    function handleInputKeyDown(event) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setSelectedIndex((index) => results.length ? (index + 1) % results.length : 0);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setSelectedIndex((index) => results.length ? (index - 1 + results.length) % results.length : 0);
      } else if (event.key === "Enter") {
        event.preventDefault();
        choose(results[activeIndex]);
      }
    }
    input.addEventListener("keydown", handleInputKeyDown);
    return () => input.removeEventListener("keydown", handleInputKeyDown);
  }, [choose, inputRef, open, results, activeIndex]);

  useEffect(() => {
    if (!open) return;
    const selected = document.querySelector('[data-testid="universal-search-palette"] [aria-selected="true"]');
    selected?.scrollIntoView?.({ block: "nearest" });
  }, [open, activeIndex, results]);

  useEffect(() => {
    if (!open) return undefined;
    function onKeyDown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !anchored) return undefined;
    function handleOutsideMouseDown(event) {
      const palette = document.querySelector('[data-testid="universal-search-palette"]');
      const trigger = document.querySelector('[data-testid="universal-search-trigger"]');
      if (palette?.contains(event.target) || trigger?.contains(event.target)) return;
      onClose();
    }
    document.addEventListener("mousedown", handleOutsideMouseDown);
    return () => document.removeEventListener("mousedown", handleOutsideMouseDown);
  }, [anchored, onClose, open]);

  if (!open) return null;

  async function removeRecent(item) {
    setMutationError(null);
    try {
      await removeRecentView(item.type, item.id);
      setReloadKey((key) => key + 1);
      inputRef?.current?.focus();
    } catch {
      setMutationError("Could not update recent views. Try again.");
    }
  }

  async function clearRecents() {
    setMutationError(null);
    try {
      await clearRecentViews();
      setReloadKey((key) => key + 1);
      inputRef?.current?.focus();
    } catch {
      setMutationError("Could not clear recent views. Try again.");
    }
  }

  return (
    <div
      data-testid="universal-search-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          event.stopPropagation();
          onClose();
        }
      }}
      style={{
        ...(anchored
          ? {
              position: "absolute",
              top: "calc(100% - 1px)",
              left: 0,
              width: "100%",
              zIndex: LAYERS.modal,
            }
          : {
              position: "fixed",
              inset: 0,
              zIndex: LAYERS.modal,
              display: "grid",
              placeItems: "start center",
              paddingTop: "min(18vh, 150px)",
              background: "rgba(15, 23, 42, .34)",
            }),
      }}
    >
      <section
        role={anchored ? "region" : "dialog"}
        aria-modal={anchored ? undefined : "true"}
        aria-label="Universal search"
        data-testid="universal-search-palette"
        onMouseDown={(event) => event.stopPropagation()}
        style={{
          width: "100%",
          maxHeight: "min(620px, calc(100vh - 48px))",
          overflow: "hidden",
          background: T.card,
          border: `1px solid ${T.border}`,
          borderRadius: anchored ? "0 0 12px 12px" : 16,
          boxShadow: anchored ? "0 14px 28px rgba(15,23,42,.16)" : "0 28px 80px rgba(15,23,42,.3)",
        }}
      >
        <div style={{ overflowY: "auto", maxHeight: "min(530px, calc(100vh - 130px))", padding: "8px 8px 10px" }}>
          {showAsk && (
            <button type="button" data-testid="universal-search-ask"
              className="universal-search-result-row"
              aria-selected={activeIndex === 0}
              onMouseEnter={() => setSelectedIndex(0)}
              onFocus={() => setSelectedIndex(0)}
              onClick={() => choose(results[0])}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 10,
                padding: 10, border: 0, borderRadius: 10, textAlign: "left", cursor: "pointer",
                background: activeIndex === 0 ? T.blueSoft : T.lightGray, color: T.dark }}>
              <span style={{ width: 28, height: 28, display: "grid", placeItems: "center", borderRadius: 7, background: T.card, color: T.blue, flexShrink: 0 }}>
                <Sparkles size={16} aria-hidden="true" />
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: T.fsBase, fontWeight: T.fwSemibold }}>Ask: '{query.trim()}'</span>
                <span style={{ display: "block", marginTop: 2, color: T.mid, fontSize: T.fsSm }}>Continue in Ask Portfolio</span>
              </span>
            </button>
          )}
          {status === "loading" && <div data-testid="universal-search-loading" style={{ padding: "24px 14px", color: T.mid }}>Searching…</div>}
          {status === "error" && <div data-testid="universal-search-error" style={{ padding: "24px 14px", color: T.red }}>Search is unavailable. Try again.</div>}
          {mutationError && <div data-testid="universal-search-mutation-error" style={{ padding: "8px 14px", color: T.red }}>{mutationError}</div>}
          {status === "ready" && !hasQuery && !hasRecentViews && (
            <section
              data-testid="universal-search-guidance"
              aria-label="Search examples"
              style={{ padding: "14px 14px 12px", borderBottom: `1px solid ${T.border}` }}
            >
              <div style={{ marginBottom: 4, color: T.light, fontSize: T.fsXs, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".06em" }}>Recent</div>
              <div style={{ marginBottom: 18, color: T.mid, fontSize: T.fsSm }}>No recent views yet.</div>
              <div style={{ marginBottom: 2, color: T.dark, fontSize: T.fsBase, fontWeight: T.fwBold }}>
                What are you looking for?
              </div>
              <div style={{ marginBottom: 12, color: T.mid, fontSize: T.fsSm, lineHeight: 1.5 }}>
                Or try typing
              </div>
              <div data-testid="universal-search-first-visit-examples">
                {FIRST_VISIT_EXAMPLES.map((example) => (
                  <div
                    key={example.query}
                    data-testid={`universal-search-example-${example.query.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 10,
                      padding: "7px 0",
                      borderTop: example.query === FIRST_VISIT_EXAMPLES[0].query ? 0 : `1px solid ${T.lightGray}`,
                      cursor: "default",
                    }}
                  >
                    <span style={{ flexShrink: 0, color: T.searchExampleInk, fontWeight: T.fwSemibold, fontSize: T.fsSm }}>
                      “{example.query}”
                    </span>
                    <span style={{ color: T.mid, fontSize: T.fsSm }}>{example.note}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
          {status === "ready" && payloadQuery === query && hasQuery && entityResults.length === 0 && (
            <div data-testid="universal-search-no-results" style={{ padding: "24px 14px", color: T.mid }}>
              <div style={{ color: T.dark, fontSize: T.fsBase, fontWeight: T.fwSemibold }}>
                No matches for “{query.trim()}”
              </div>
              <p style={{ margin: "7px 0 12px", fontSize: T.fsSm, lineHeight: 1.5 }}>
                {showAsk
                  ? "No matching records. Try a name, or continue with your question in Ask Portfolio."
                  : "Try typing a name, like London, Angel Court, or Headcount Trend."}
              </p>
              <div style={{ marginBottom: 8, color: T.light, fontSize: T.fsXs, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".06em" }}>
                Or jump to
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {SEARCH_BROWSE_DESTINATIONS
                  .filter((destination) => categoryVisibility[destination.key] !== false)
                  .map((destination) => {
                    const Icon = destination.icon;
                    return (
                      <a
                        key={destination.key}
                        href={destination.path}
                        data-testid={`universal-search-browse-${destination.key}`}
                        aria-label={`Browse ${destination.label}`}
                        onClick={(event) => {
                          event.preventDefault();
                          onNavigate?.(destination.path);
                          onClose();
                        }}
                        onMouseEnter={(event) => {
                          event.currentTarget.style.background = T.blueSoft;
                          event.currentTarget.style.borderColor = T.blue;
                        }}
                        onMouseLeave={(event) => {
                          event.currentTarget.style.background = T.lightGray;
                          event.currentTarget.style.borderColor = T.border;
                        }}
                        style={{
                          flex: "1 1 140px",
                          minWidth: 140,
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "10px 12px",
                          border: `1px solid ${T.border}`,
                          borderRadius: 11,
                          background: T.lightGray,
                          color: T.blue,
                          fontSize: T.fsSm,
                          fontWeight: T.fwSemibold,
                          textDecoration: "none",
                        }}
                      >
                        <span style={{ width: 30, height: 30, display: "grid", placeItems: "center", flexShrink: 0, borderRadius: 8, border: `1px solid ${T.border}`, background: T.card }}>
                          <Icon size={16} aria-hidden="true" />
                        </span>
                        <span style={{ color: T.dark }}>{destination.label}</span>
                        <ArrowRight size={15} aria-hidden="true" style={{ marginLeft: "auto", color: T.light }} />
                      </a>
                    );
                  })}
              </div>
            </div>
          )}
          {status === "ready" && payloadQuery === query && visibleGroups.map((group) => {
            const isRecentGroup = group.key === "recent";
            if (!group.results?.length) return null;
            return (
              <div key={group.key} data-testid={`universal-search-group-${group.key}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 10px 5px", color: T.light, fontSize: T.fsXs, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".06em" }}>
                  {isRecentGroup ? "Recent" : group.label}
                  {isRecentGroup && (
                    <button
                      type="button"
                      data-testid="universal-search-clear-recents"
                      aria-label="Clear recents"
                      onClick={clearRecents}
                      style={{
                        marginLeft: "auto",
                        border: 0,
                        padding: "2px 4px",
                        background: "transparent",
                        color: T.mid,
                        cursor: "pointer",
                        fontSize: T.fsXs,
                        fontWeight: T.fwSemibold,
                        textTransform: "none",
                        letterSpacing: 0,
                      }}
                    >
                      Clear recents
                    </button>
                  )}
                </div>
                {group.results.map((item) => {
                  // The flattened item is a copy, so locate by stable group/id.
                  const flatIndex = results.findIndex((entry) => entry.groupKey === group.key && entry.id === item.id);
                  const Icon = ICONS[item.icon] || ICONS[item.type] || Search;
                  const selected = flatIndex === activeIndex;
                  const recentKey = `${item.type}:${item.id}`;
                  const showRemove = isRecentGroup
                    && (hoveredRecentKey === recentKey || focusedRecentKey === recentKey || selected);
                  return (
                    <div
                      key={`${group.key}-${item.id}`}
                      onMouseEnter={() => {
                        setSelectedIndex(flatIndex);
                        if (isRecentGroup) setHoveredRecentKey(recentKey);
                      }}
                      onMouseLeave={() => {
                        if (isRecentGroup) setHoveredRecentKey(null);
                      }}
                      style={{ display: "flex", alignItems: "stretch", gap: 4 }}
                    >
                      <button
                        type="button"
                        data-testid={`universal-search-result-${item.id}`}
                        className="universal-search-result-row"
                        aria-selected={selected}
                        onFocus={() => {
                          setSelectedIndex(flatIndex);
                          if (isRecentGroup) setFocusedRecentKey(recentKey);
                        }}
                        onBlur={() => {
                          if (isRecentGroup) setFocusedRecentKey(null);
                        }}
                        onClick={() => choose(item)}
                        style={{
                          flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 11,
                          padding: "10px", border: 0, borderRadius: 10, textAlign: "left",
                          background: selected ? T.blueSoft : "transparent",
                          color: T.dark, cursor: "pointer",
                        }}
                      >
                        <span style={{ width: 28, height: 28, display: "grid", placeItems: "center", flexShrink: 0, borderRadius: 7, background: selected ? T.card : T.lightGray, color: selected ? T.blue : T.mid }}>
                          <Icon size={16} aria-hidden="true" />
                        </span>
                        <span style={{ minWidth: 0, flex: 1 }}>
                          <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: T.fsBase, fontWeight: T.fwSemibold }}>{item.title}</span>
                          {(item.subtitle || item.context) && <span style={{ display: "block", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: T.mid, fontSize: T.fsSm }}>{[item.subtitle, item.context].filter(Boolean).join(" · ")}</span>}
                        </span>
                        {item.status && item.type !== "report" && (
                          <span
                            data-testid={`universal-search-status-${item.id}`}
                            style={{
                              padding: "3px 7px",
                              borderRadius: 999,
                              border: `1px solid ${isInactiveStatus(item.status) ? T.border : T.blueSoft}`,
                              background: isInactiveStatus(item.status) ? T.lightGray : T.blueSoft,
                              color: isInactiveStatus(item.status) ? T.mid : T.blue,
                              fontSize: T.fsXs,
                              fontWeight: T.fwSemibold,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatStatus(item.status)}
                          </span>
                        )}
                        <span style={{ color: T.light, fontSize: T.fsXs, whiteSpace: "nowrap" }}>{item.type}</span>
                      </button>
                      {isRecentGroup && (
                        <button
                          type="button"
                          data-testid={`universal-search-remove-${item.type}-${item.id}`}
                          aria-label={`Remove ${item.title} from recents`}
                          title={`Remove ${item.title} from recents`}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            removeRecent(item);
                          }}
                          style={{
                            width: 30,
                            flexShrink: 0,
                            border: 0,
                            borderRadius: 8,
                            background: showRemove ? T.lightGray : "transparent",
                            color: T.mid,
                            cursor: "pointer",
                            opacity: showRemove ? 1 : 0,
                            pointerEvents: showRemove ? "auto" : "none",
                            fontSize: T.fsMd,
                            lineHeight: 1,
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
          {status === "ready" && !hasQuery && hasRecentViews && (
            <section
              data-testid="universal-search-returning-examples"
              aria-label="More search examples"
              style={{ marginTop: 8, padding: "12px 14px 14px", borderTop: `1px solid ${T.border}` }}
            >
              <div style={{ marginBottom: 5, color: T.mid, fontSize: T.fsSm, fontWeight: T.fwSemibold }}>
                Or try typing
              </div>
              <div data-testid="universal-search-returning-example-rows">
                {returningExamples.map((example, index) => (
                  <div
                    key={example.query}
                    data-testid={`universal-search-returning-example-${example.query.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 10,
                      padding: "7px 0",
                      borderTop: index === 0 ? 0 : `1px solid ${T.lightGray}`,
                      cursor: "default",
                    }}
                  >
                    <span style={{ flexShrink: 0, color: T.searchExampleInk, fontWeight: T.fwSemibold, fontSize: T.fsSm }}>
                      “{example.query}”
                    </span>
                    <span style={{ color: T.mid, fontSize: T.fsSm }}>{example.note}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
        <footer data-testid="universal-search-footer" style={{ padding: "10px 18px", borderTop: `1px solid ${T.hairline}`, color: T.mid, fontSize: T.fsXs5 }}>
          ↑↓ to move · ↵ to open · ⌘K anywhere
        </footer>
      </section>
    </div>
  );
}